/**
 * Redpim client API
 */
import { ItemStatusLabel } from '@/app/api/types/redpim.enum'
import { Accessory, Item, Product } from '@/app/api/types/redpim.types'
import {
	CadFile,
	CalibrationCertificate,
	CatalogLeaflet,
	ComplianceCertificate,
	ExplodedView,
	SafetyInstructions,
	UserManual,
} from '@/lib/digital-twin'

// exception for fuji-tools
const BRAND =
	process.env.NEXT_PUBLIC_PROJECT === 'fuji-tools'
		? 'fuji'
		: process.env.NEXT_PUBLIC_PROJECT
const REDPIM_URL = `${process.env.NEXT_PUBLIC_REDPIM_URL}/${BRAND}`
if (!REDPIM_URL) {
	throw new Error('NEXT_PUBLIC_REDPIM_URL is not defined')
}

export type GetItemsResponse = {
	items: Item[]
	total: number
}

export type GetItemsURLParams = {
	page?: number
	pageSize?: number
	categories?: string
	uses?: string
	types?: string
	industries?: string
	quickView?: boolean
	details?: boolean
	active?: ['active' | 'inactive_out_of_production' | 'inactive_not_sold']
}

export async function fetchProduct(
	productId: string,
	abortController: AbortController = null,
): Promise<Product | null> {
	const rawProduct = await fetch(`${REDPIM_URL}/products/${productId}`, {
		signal: abortController?.signal,
	})

	return rawProduct.json()
}

export async function fetchItem(
	itemId: string,
	abortController: AbortController = null,
): Promise<Item | null> {
	const rawItem = await fetch(`${REDPIM_URL}/items/${itemId}`, {
		signal: abortController?.signal,
	})

	if (!rawItem.ok) {
		return null
	}

	return rawItem.json()
}

export async function fetchVariants(
	pimId: string,
	abortController: AbortController = null,
): Promise<Item[]> {
	let allItems = []

	const firstRes = await fetch(`${REDPIM_URL}/products/${pimId}/items`, {
		signal: abortController?.signal,
	})
	const firstData = await firstRes.json()
	const { items, total, limit } = firstData
	allItems = [...items]

	const remainingPages = Math.ceil((total - limit) / limit)

	for (let page = 1; page <= remainingPages; page++) {
		const offset = page * limit
		const res = await fetch(
			`${REDPIM_URL}/products/${pimId}/items?limit=${limit}&offset=${offset}`,
			{
				signal: abortController?.signal,
			},
		)
		const data = await res.json()
		allItems = [...allItems, ...data.items]
	}

	return allItems
}

export async function fetchAccessories(
	partNumber: string,
): Promise<Accessory[]> {
	const res = await fetch(`${REDPIM_URL}/items/${partNumber}/accessories`)
	const items = await res.json()

	// Filter out inactive accessories (out of production and not sold)
	return items.filter(
		(accessory: Accessory) =>
			accessory.statusLabel !== ItemStatusLabel.OUT_OF_PRODUCTION &&
			accessory.statusLabel !== ItemStatusLabel.NOT_SOLD,
	)
}

export async function fetchSimilars(
	pimId: string,
	type: string,
	abortController: AbortController = null,
) {
	let allItems = []

	const firstRes = await fetch(
		`${REDPIM_URL}/products/${pimId}/items?type=${type}`,
		{
			signal: abortController?.signal,
		},
	)

	const firstData = await firstRes.json()
	const { items, total, limit } = firstData
	allItems = [...items]

	const remainingPages = Math.ceil((total - limit) / limit)

	for (let page = 1; page <= remainingPages; page++) {
		const offset = page * limit
		const res = await fetch(
			`${REDPIM_URL}/products/${pimId}/items?type=${type}&limit=${limit}&offset=${offset}`,
			{
				signal: abortController?.signal,
			},
		)
		const data = await res.json()
		allItems = [...allItems, ...data.items]
	}

	return allItems
}

export async function getItems(
	data: GetItemsURLParams,
	abortController: AbortController = null,
) {
	const url = new URL(`${REDPIM_URL}/items`)
	url.searchParams.set('page', data.page ? String(data.page) : '0')
	url.searchParams.set('pageSize', data.pageSize ? String(data.pageSize) : '30')
	if (data.categories) url.searchParams.set('categories', data.categories) // cat = subcat
	if (data.uses) url.searchParams.set('uses', data.uses)
	if (data.types) url.searchParams.set('types', data.types)
	if (data.industries) url.searchParams.set('industries', data.industries)
	if (data.active) url.searchParams.set('active', data.active.join(','))

	url.searchParams.set(
		'brands',
		process.env.NEXT_PUBLIC_PROJECT === 'fuji-tools'
			? 'fuji'
			: process.env.NEXT_PUBLIC_PROJECT,
	)

	const res = await fetch(url.toString(), { signal: abortController?.signal })
	return res.json() as Promise<GetItemsResponse>
}

// -- Legacy Redpim API --

export type GetProductWithItemsResponse = {
	items: Item[]
	'0': {
		productid: string
		categoryid: string
		productname: string
		producttype: string
		thumbnail: string
		shortdescription: string
		description: string
		usp: string
		slogan: string
		benefits: string[]
		features: null
		keywords: null
		specificationFilters: {
			name: { [key: string]: string }
			attributeId: string
			unitCategory: string
			specFieldTypeId: string
			value: string
		}[]
		itemCount: string
		items: string
	}
}

export type GetProductWithoutItemResponse = [
	{
		productid: string
		categoryid: string
		productname: string
		producttype: string
		thumbnail: string
		shortdescription: string
		description: string
		usp: string
		slogan: string
		benefits: string[]
		features: null
		keywords: null
		specifications: {
			name: { [key: string]: string }
			attributeId: string
			unitCategory: 'Metric' | 'Imperial' | 'Universal'
			value: string
		}[]
		itemCount: string
		items: string
		specificationBlocks: {
			[key in 'Metric' | 'Imperial' | 'Universal']: {
				[key: string]: string
			}
		}
	},
]

export async function getProduct(
	productId: string,
	onlyActiveItems: boolean,
	abortController: AbortController = null,
): Promise<Product> {
	const productResponse = await fetch(`${REDPIM_URL}/products/${productId}`, {
		signal: abortController?.signal,
	})

	const product = await productResponse.json()
	let items = await getItemsOfProduct(productId, abortController)

	if (onlyActiveItems) {
		items = items.filter((item) => item.statusLabel === ItemStatusLabel.ACTIVE)
	}

	return { ...product, items }
}

export async function getAllProducts(abortController: AbortController = null) {
	let allProducts = []
	const firstRes = await fetch(`${REDPIM_URL}/products`, {
		signal: abortController?.signal,
	})

	const firstData = await firstRes.json()
	const { products, total, limit } = firstData
	allProducts = [...products]
	const remainingPages = Math.ceil((total - limit) / limit)

	for (let page = 1; page <= remainingPages; page++) {
		const offset = page * limit
		const res = await fetch(
			`${REDPIM_URL}/products?limit=${limit}&offset=${offset}`,
			{
				signal: abortController?.signal,
			},
		)
		const data = await res.json()
		allProducts = [...allProducts, ...data.products]
	}

	return allProducts
}
export async function getItemsOfProduct(
	productId: string,
	abortController: AbortController = null,
) {
	let allItems = []

	const firstRes = await fetch(`${REDPIM_URL}/products/${productId}/items`, {
		signal: abortController?.signal,
	})

	const firstData = await firstRes.json()
	const { items, total, limit } = firstData
	allItems = [...items]

	const remainingPages = Math.ceil((total - limit) / limit)

	for (let page = 1; page <= remainingPages; page++) {
		const offset = page * limit
		const res = await fetch(
			`${REDPIM_URL}/products/${productId}/items?limit=${limit}&offset=${offset}`,
			{
				signal: abortController?.signal,
			},
		)
		const data = await res.json()
		allItems = [...allItems, ...data.items]
	}

	return allItems
}

export async function fetchSpareParts(
	partNumber: string,
	abortController: AbortController = null,
): Promise<
	{
		partNumber: string
		model: string
	}[]
> {
	try {
		const res = await fetch(
			`${process.env.NEXT_PUBLIC_DESOUTTER_TOOL_API_URL}/product/spares`,
			{
				signal: abortController?.signal,
				method: 'POST',
				body: JSON.stringify({ query: partNumber, type: 'item' }),
				headers: {
					'Content-Type': 'application/json',
					'x-api-key': process.env.NEXT_PUBLIC_DESOUTTER_TOOL_API_KEY,
				},
			},
		)

		if (!res.ok) {
			console.error('Error fetching spare parts:', res.statusText)
			return []
		}

		const items = await res.json()

		// Check if items and items.items exist and have the expected structure
		if (
			!items ||
			!items.items ||
			!Array.isArray(items.items) ||
			items.items.length === 0
		) {
			console.warn('No spare parts found for part number:', partNumber)
			return []
		}

		// Check if the first item has relatedSpares
		if (!items.items[0] || !items.items[0].relatedSpares) {
			console.warn('No related spares found for part number:', partNumber)
			return []
		}

		return items.items[0].relatedSpares
	} catch (error) {
		console.error('Error fetching spare parts:', error)
		return []
	}
}

export async function search(params: {
	query: string
	querytype?: string
	locale?: string
	limit?: number
}) {
	const { query, querytype, locale, limit = 10 } = params

	const urlSearch = new URL(`${REDPIM_URL}/items/search`)
	urlSearch.searchParams.set('query', query)

	if (querytype) urlSearch.searchParams.set('querytype', querytype)
	if (locale) urlSearch.searchParams.set('locale', locale)
	if (limit) urlSearch.searchParams.set('limit', String(limit))

	const res = await fetch(urlSearch.toString())

	return res.json()
}

export type DocumentAPIResponse = {
	cadFiles?: {
		'@odata.context': string
		'@odata.count': number
		value: CadFile[]
	}
	userManuals?: {
		'@odata.context': string
		'@odata.count': number
		value: UserManual[]
	}
	safetyInstructions?: {
		'@odata.context': string
		'@odata.count': number
		value: SafetyInstructions[]
	}
	calibrationCertificates?: {
		tmss: any
		original: {
			value: CalibrationCertificate[]
		}
	}
	complianceCertificates?: {
		'@odata.context': string
		'@odata.count': number
		value: ComplianceCertificate[]
	}
	explodedViews?: {
		'@odata.context': string
		'@odata.count': number
		value: ExplodedView[]
	}
	catalogsLeaflets: {
		'@odata.context': string
		'@odata.count': number
		value: CatalogLeaflet[]
	}
	partNumber: string
}
export async function getDocuments(
	partNumber: string,
): Promise<DocumentAPIResponse> {
	const emptyDocumentData = {
		cadFiles: {
			'@odata.context': '',
			'@odata.count': 0,
			value: [],
		},
		userManuals: {
			'@odata.context': '',
			'@odata.count': 0,
			value: [],
		},
		complianceCertificates: {
			'@odata.context': '',
			'@odata.count': 0,
			value: [],
		},
		safetyInstructions: {
			'@odata.context': '',
			'@odata.count': 0,
			value: [],
		},
		explodedViews: {
			'@odata.context': '',
			'@odata.count': 0,
			value: [],
		},
		catalogsLeaflets: {
			'@odata.context': '',
			'@odata.count': 0,
			value: [],
		},
		partNumber: '',
	}
	try {
		const res = await fetch(
			`${process.env.NEXT_PUBLIC_DIGITAL_TWIN_URL}/api/documents/${partNumber}`,
			{
				headers: {
					'X-API-KEY': process.env.NEXT_PUBLIC_DESOUTTER_API_KEY,
				},
			},
		)
		if (res.ok) {
			return res.json()
		} else {
			console.error('Error fetching documents:', res.statusText)
			return emptyDocumentData
		}
	} catch (error) {
		console.error('Error fetching documents:', error)
		return emptyDocumentData
	}
}

export const convertedLanguages = {
	en: 'en-US',
	es: 'es-ES',
	fr: 'fr-FR',
	de: 'de-DE',
	en_US: 'en-US',
	en_GB: 'en-GB',
	en_IN: 'en-IN',
	en_SE: 'sv-SE',
	en_TH: 'th-TH',
	fr_FR: 'fr-FR',
	cs_CZ: 'cs-CZ',
	de_AT: 'de-AT',
	de_CH: 'de-CH',
	de_DE: 'de-DE',
	es_AR: 'es-AR',
	es_CL: 'es-CL',
	es_CO: 'es-CO',
	es_ES: 'es-ES',
	es_MX: 'es-MX',
	es_PE: 'es-PE',
	fr_BE: 'fr-BE',
	fr_CA: 'fr-CA',
	fr_CH: 'fr-CH',
	hu_HU: 'hu-HU',
	it_CH: 'it-CH',
	it_IT: 'it-IT',
	ja_JP: 'ja-JP',
	ko_KR: 'ko-KR',
	nl_BE: 'nl-BE',
	nl_NL: 'nl-NL',
	pl_PL: 'pl-PL',
	pt_BR: 'pt-BR',
	pt_PT: 'pt-PT',
	ro_RO: 'ro-RO',
	sk_SK: 'sk-SK',
	sl_SI: 'sl-SI',
	sv_SE: 'sv-SE',
	tr_TR: 'tr-TR',
	zh_CN: 'zh-CN',
	en_ID: 'en-IN',
	en_ZA: 'en-ZA',
}
